import { Link } from "react-router-dom";
import type { IssueDetail } from "../lib/segment-types";
import { relativeTime } from "../lib/dates";
import { Bidi, issueStatusPill, StatusPill } from "./primitives";

/**
 * The home screen's hero card — the Altibbi tool standard's signature (§6).
 *
 * A light band on --brand-50, never navy or solid blue. On the start side: the
 * date, a greeting that is actually useful, the same line in Arabic beneath it
 * (the bilingual pairing the standard calls the Altibbi signature), one
 * sentence, and the screen's single primary action beside one secondary. On
 * the end side, a white card for the next thing that happens in this tool.
 *
 * Every word is drawn from real data. The greeting counts the drafts and
 * reviews that exist; the card shows the newsletter that is actually next;
 * nothing is filled in when there is nothing to say.
 */
export function HomeHero({
  issues,
  subscribers,
  canCreate,
  onCreate,
}: {
  issues: IssueDetail[];
  subscribers: number | null;
  canCreate: boolean;
  onCreate: () => void;
}) {
  const greeting = greetingFor(issues);
  const next = nextThing(issues);

  return (
    <section className="ui-hero" aria-labelledby="ui-hero-title">
      <HeroTexture />

      <div className="ui-hero-start">
        <div className="ui-hero-date">{shortDate(new Date())}</div>
        <h1 id="ui-hero-title" className="ui-hero-title">{greeting.en}</h1>
        <p className="ui-hero-title-ar" lang="ar" dir="rtl">{greeting.ar}</p>
        <p className="ui-hero-sub">
          {subscribers === null
            ? "Build, review and send the Altibbi newsletter."
            : `${subscribers.toLocaleString("en-US")} newsletter subscribers are waiting for the next issue.`}
        </p>
        <div className="ui-hero-actions">
          <button
            type="button"
            className="nl-btn-primary ui-hero-primary"
            disabled={!canCreate}
            title={canCreate ? undefined : "Your role cannot create newsletters"}
            onClick={onCreate}
          >
            New newsletter
          </button>
          <Link className="nl-btn-secondary" to="/content">Open newsletters</Link>
        </div>
      </div>

      <div className="ui-hero-next">
        {next ? (
          <>
            <StatusPill tone={issueStatusPill(next.issue.status).tone}>
              {issueStatusPill(next.issue.status).label}
            </StatusPill>
            <div className="ui-hero-next-kicker">{next.kicker}</div>
            <h2 className="ui-hero-next-title">
              <Bidi>{next.issue.title || next.issue.subject || "Untitled newsletter"}</Bidi>
            </h2>
            <div className="ui-hero-next-time">Updated {relativeTime(next.issue.updated_at)}</div>
            <div className="ui-hero-insets">
              <div className="ui-hero-inset">
                <span className="ui-hero-inset-value">{sectionCount(next.issue)}</span>
                <span className="ui-hero-inset-label">Sections</span>
              </div>
              <div className="ui-hero-inset">
                <span className="ui-hero-inset-value">{next.issue.categories?.length ?? 0}</span>
                <span className="ui-hero-inset-label">Audience topics</span>
              </div>
            </div>
            <Link className="nl-btn-secondary ui-hero-next-open" to={`/content/${next.issue.id}`}>
              Open newsletter
            </Link>
          </>
        ) : (
          <>
            <StatusPill tone="neutral">Nothing scheduled</StatusPill>
            <h2 className="ui-hero-next-title">No newsletter in progress</h2>
            <div className="ui-hero-next-time">Start one and it will show here.</div>
          </>
        )}
        <PulsePair className="ui-hero-crest" />
      </div>
    </section>
  );
}

/* ── What the greeting says ─────────────────────────────────────────────────
   A useful line beats a slogan (standard §11), so it says what is waiting. */

function arabicCount(n: number, one: string, two: string, few: string, many: string): string {
  if (n === 1) return one;
  if (n === 2) return two;
  if (n >= 3 && n <= 10) return `${n} ${few}`;
  return `${n} ${many}`;
}

export function greetingFor(issues: IssueDetail[]): { en: string; ar: string } {
  const inReview = issues.filter((i) => i.status === "in_review").length;
  const drafts = issues.filter((i) => i.status === "draft").length;
  if (inReview > 0) {
    return {
      en: inReview === 1 ? "One newsletter is waiting for review." : `${inReview} newsletters are waiting for review.`,
      ar: `${arabicCount(inReview, "نشرة واحدة", "نشرتان", "نشرات", "نشرة")} بانتظار المراجعة.`,
    };
  }
  if (drafts > 0) {
    return {
      en: drafts === 1 ? "One draft is in progress." : `${drafts} drafts are in progress.`,
      ar: `${arabicCount(drafts, "مسودة واحدة", "مسودتان", "مسودات", "مسودة")} قيد العمل.`,
    };
  }
  return { en: "Nothing is waiting on you.", ar: "لا شيء بانتظارك." };
}

/* ── What happens next ─────────────────────────────────────────────────────
   The single most useful thing to look at: whatever is closest to going out. */

const NEXT_ORDER: { status: string; kicker: string }[] = [
  { status: "scheduled", kicker: "Next send" },
  { status: "in_review", kicker: "Waiting for review" },
  { status: "approved", kicker: "Ready to schedule" },
  { status: "draft", kicker: "Draft in progress" },
];

export function nextThing(issues: IssueDetail[]): { issue: IssueDetail; kicker: string } | null {
  for (const { status, kicker } of NEXT_ORDER) {
    const match = issues
      .filter((i) => i.status === status)
      .sort((a, b) => (Date.parse(b.updated_at) || 0) - (Date.parse(a.updated_at) || 0))[0];
    if (match) return { issue: match, kicker };
  }
  return null;
}

function sectionCount(issue: IssueDetail): number {
  return (issue.sections ?? []).filter((s) => s.type !== "footer" && s.type !== "category").length;
}

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
/** "Thu, 15 Oct" — the standard's date form for an English interface (§8). */
export function shortDate(d: Date): string {
  return `${DAYS[d.getDay()]}, ${d.getDate()} ${MONTHS[d.getMonth()]}`;
}

/* ── Illustration, from the Brand Hub primitives (§7) ─────────────────────── */

const PETAL = "M0 0 C 20 12.96, 18.8 35.04, 0 48 C -18.8 35.04, -20 12.96, 0 0 Z";

/** The pulse-pair crest: two petals from one point and a solid dot. */
function PulsePair({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 120 120" aria-hidden="true" focusable={false}>
      <path d={PETAL} transform="translate(60 82) rotate(152)" fill="none" stroke="var(--brand-500)" strokeWidth="6" strokeLinejoin="round" />
      <path d={PETAL} transform="translate(60 82) rotate(208)" fill="none" stroke="var(--brand-500)" strokeWidth="6" strokeLinejoin="round" />
      <circle cx="60" cy="82" r="3.6" fill="var(--brand-500)" />
      <path d="M30 98 C 43.2 98, 50.4 90, 60 90 C 69.6 90, 76.8 98, 90 98" fill="none" stroke="var(--brand-500)" strokeWidth="5.4" strokeLinecap="round" />
    </svg>
  );
}

/** The pulse-trail along the bottom and a faint halo + swash off the top end corner. */
function HeroTexture() {
  const petal = "M0 0 C 3.6 3.24, 3.384 8.76, 0 12 C -3.384 8.76, -3.6 3.24, 0 0 Z";
  const xs = [40, 115, 190, 265, 340, 415, 490, 565, 640, 715, 790, 865, 940, 1015, 1090, 1160];
  return (
    <>
      <svg className="ui-hero-trail" viewBox="0 0 1200 80" preserveAspectRatio="none" aria-hidden="true" focusable={false}>
        <path d="M0 56 C 300 56, 420 24, 600 24 C 780 24, 900 56, 1200 56" fill="none" stroke="var(--brand-200)" strokeWidth="2" strokeLinecap="round" opacity="0.7" />
        {xs.map((x, i) => {
          const y = 40 + 16 * Math.cos((2 * Math.PI * x) / 1200) - 14;
          const live = i === 9;
          return (
            <path key={x} d={petal} transform={`translate(${x} ${y.toFixed(1)})`}
              fill={live ? "var(--brand-500)" : "none"} stroke="var(--brand-200)"
              strokeWidth="2" strokeLinejoin="round" opacity={live ? 1 : 0.7} />
          );
        })}
      </svg>
      <svg className="ui-hero-halo" viewBox="0 0 96 96" aria-hidden="true" focusable={false}>
        <circle cx="48" cy="46" r="30" fill="none" stroke="var(--brand-100)" strokeWidth="6" />
        <path d="M24 58 C 34.56 58, 40.32 44, 48 44 C 55.68 44, 61.44 58, 72 58" fill="none" stroke="var(--brand-100)" strokeWidth="5" strokeLinecap="round" />
      </svg>
    </>
  );
}
